"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useSetAtom } from "jotai";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import LogoIcon from "@/assets/svg/popmart-logo.svg";
import { NavigationMenu, NavigationMenuList } from "@/components/business/header/navigation-menu";
import QuickChatButton from "@/components/pop-ui/quick-chat-button";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { getCurrentAreaInClient, getCurrentLanguageInClient, useLocaleRouter } from "@/lib/utils.client";
import { useORPC } from "@/providers/orpc-provider";
import { textBannerVisibleAtom } from "@/store/textBannerAtom";
import type { CmsContentHeader } from "../cms/schema";
import { getTranslatedText } from "../cms/utils.server";
import { CartButton } from "../shopping-cart/components/cart-button";
import { UserAccountInHeader } from "../users/account/user-account";
import { WishButton } from "../wish-list/wish-button";
import { SearchComponent } from "./components/header-search";
import { NavItemRenderer } from "./components/nav-item-renderer";
import { MobileNav } from "./mobile-nav";

// 客户端组件
export function HeaderClient(t0) {
  const $ = _c(16);
  const {
    className
  } = t0;
  const area = getCurrentAreaInClient();
  const lang = getCurrentLanguageInClient();
  const orpc = useORPC();
  const router = useLocaleRouter();
  const t1 = orpc.cms.content.public_findLiveWithPagination.queryOptions({
    input: {
      group: "header-batch-query",
      area: area?.toUpperCase(),
      includeChildren: true,
      platform: "web"
    }
  });
  let t2;
  if ($[0] !== t1) {
    t2 = {
      ...t1,
      staleTime: 300
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    data: headerBatchResponse
  } = useQuery(t2);
  const headerBatch = (headerBatchResponse as any)?.items || [];
  const headerConfig = headerBatch.find(_temp);
  const bannerResponse = headerBatch.find(_temp2);
  const banners = bannerResponse?.children || [];
  const setBannerVisible = useSetAtom(textBannerVisibleAtom);
  useEffect(() => {
    if (banners.length === 0) {
      setBannerVisible(false);
    }
  }, [banners.length, setBannerVisible]);
  let navItems = [];
  if (headerConfig?.children) {
    navItems = headerConfig.children as unknown as CmsContentHeader[];
    navItems = navItems.map(item_1 => {
      const titleTrans = typeof item_1.title_trans === "string" ? item_1.title_trans : getTranslatedText(lang, item_1.title_trans);
      return {
        ...item_1,
        title_trans: titleTrans,
        data: item_1.type === "textOnly" ? {
          ...item_1.data,
          textItems: (item_1.data as any).textItems?.map(textItem => ({
            ...textItem,
            text: getTranslatedText(lang, textItem.text)
          }))
        } : item_1.type === "link" ? item_1.data : {
          ...item_1.data,
          imageTextItems: (item_1.data as any).imageTextItems?.map(imageTextItem => ({
            ...imageTextItem,
            text: getTranslatedText(lang, imageTextItem.text),
            image: {
              ...imageTextItem.image,
              alt: getTranslatedText(lang, imageTextItem.image?.alt)
            }
          }))
        }
      } as unknown as CmsContentHeader;
    });
  }
  const isMobile = useIsMobile();
  const pathname = usePathname();
  let t3;
  if ($[2] !== pathname) {
    t3 = pathname.replace(/^\/[a-zA-Z-]+/, "") === "" || pathname.replace(/^\/[a-zA-Z-]+/, "") === "/";
    $[2] = pathname;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const isHomePage = t3;
  const [hoveredItem, setHoveredItem] = useState(null);
  const [prevHoveredItem, setPrevHoveredItem] = useState(null);
  const [hoverKey, setHoverKey] = useState(0);
  let t4;
  if ($[4] !== hoveredItem) {
    t4 = itemId => {
      if (itemId !== hoveredItem) {
        setPrevHoveredItem(hoveredItem);
        setHoveredItem(itemId);
        setHoverKey(_temp3);
      }
    };
    $[4] = hoveredItem;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleItemHover = t4;
  const limitedNavItems = navItems.slice(0, 12);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  let t5;
  let t6;
  if ($[6] !== lastScrollY) {
    t5 = () => {
      const handleScroll = () => {
        const currentScrollY = window.scrollY;
        if (currentScrollY > lastScrollY && currentScrollY > 100) {
          setHeaderVisible(false);
        } else {
          if (currentScrollY < lastScrollY) {
            setHeaderVisible(true);
          }
        }
        setLastScrollY(currentScrollY);
      };
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    };
    t6 = [lastScrollY];
    $[6] = lastScrollY;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  const t7 = !isMobile && <div className={cn("w-full", isMobile ? "" : "sticky left-0 right-0 z-50 top-0")}>{!isMobile && <div className={cn("w-full sticky left-0 right-0 z-50 top-0 transition-transform duration-300", headerVisible ? "translate-y-0" : "-translate-y-[4.25rem]")}><div className="w-full bg-background overflow-hidden"><div className="mx-auto px-10 flex items-center justify-between relative h-17"><div className="flex items-center justify-start"><SearchComponent className="max-w-xs" /></div><div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2"><div onClick={() => {
              if (isHomePage) {
                window.location.reload();
              } else {
                router.push("/");
              }
            }} className="flex items-center cursor-pointer"><LogoIcon className="w-26 h-8" /></div></div><div className="flex items-center justify-end gap-4 cursor-pointer"><QuickChatButton /><UserAccountInHeader /><WishButton /><CartButton /></div></div></div>{limitedNavItems?.length > 0 && <div className="w-full border-t border-b border-[#eee] dinpro text-primary bg-background h-12"><NavigationMenu><NavigationMenuList className="flex flex-wrap justify-center h-12 items-center overflow-hidden">{limitedNavItems.map(item_2 => <NavItemRenderer key={item_2.id} item={item_2} hoveredItem={hoveredItem} prevHoveredItem={prevHoveredItem} hoverKey={hoverKey} onItemHover={handleItemHover} />)}</NavigationMenuList></NavigationMenu></div>}</div>}</div>;
  let t8;
  if ($[9] !== className || $[10] !== isMobile || $[11] !== limitedNavItems) {
    t8 = isMobile && <motion.header className={cn("w-full bg-background sticky top-0 left-0 right-0 z-50", className)}><MobileNav navItems={limitedNavItems} /></motion.header>;
    $[9] = className;
    $[10] = isMobile;
    $[11] = limitedNavItems;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t7 || $[14] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}
function _temp3(prev) {
  return prev + 1;
}
function _temp2(item_0) {
  return item_0.alias === "text-banner-config";
}
function _temp(item) {
  return item.alias === "header-config";
}
export { ImageTextItemRenderer } from "./components/image-text-item-renderer";
// 导出组件
export { NavItemRenderer } from "./components/nav-item-renderer";

// 默认导出HeaderClient
export { HeaderClient as default };